<script>
import dayjs from '@/services/date'
import baseMixin from '@/mixins/baseMixin'
import { mapGetters } from 'vuex'
import InterestChargesDialog from './interest-charges-dialog.vue'

export default {
  name: 'OverdueInvoicesClientDialog',
  components: {
    InterestChargesDialog,
  },
  mixins: [baseMixin],
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    client: {
      type: Object,
      required: true,
    },
  },
  data: (vm) => {
    return {
      headers: [
        {
          text: vm.$t('finance.overdueInvoices.columnHeadings.invoiceNo'),
          value: 'invoiceNo',
        },
        {
          text: vm.$t('finance.overdueInvoices.columnHeadings.daysOverdue'),
          value: 'daysOverdue',
        },
        {
          text: vm.$t('finance.overdueInvoices.columnHeadings.invoiceDate'),
          value: 'invoiceDate',
        },
        {
          text: vm.$t('finance.overdueInvoices.columnHeadings.invoiceValue'),
          value: 'invoiceValueExTax',
          align: 'end',
        },
        {
          text: vm.$t(
            'finance.overdueInvoices.columnHeadings.outstandingInterest'
          ),
          value: 'existingInterestChargedExTax',
          align: 'end',
        },
        {
          text: vm.$t(
            'finance.overdueInvoices.columnHeadings.unchargedInterest'
          ),
          value: 'unchargedInterestExTax',
          align: 'end',
          cellClass: 'font-weight-bold',
        },
      ],
      openedInterestChargesDialogTitle: '',
      showInterestChargesDialog: false,
      openedInterestCharges: [],
    }
  },
  computed: {
    ...mapGetters({
      invoiceCenterLocale: 'finance/currentInvoiceCenterLocale',
    }),
  },
  methods: {
    closeDialog() {
      this.$emit('close-dialog')
    },
    onViewExistingChargesClick(overdueInvoice) {
      this.openedInterestCharges = overdueInvoice.existingCharges
      this.openedInterestChargesDialogTitle = `Invoice No: ${overdueInvoice.invoiceNo}`
      this.showInterestChargesDialog = true
    },
    onCloseInterestChargesDialog() {
      this.showInterestChargesDialog = false
      this.openedInterestCharges = []
    },
    localisedInvoiceDate(val) {
      return this.$d(dayjs(val), 'dateFormatted', this.invoiceCenterLocale)
    },
    formatDaysOverdue(daysOverdue) {
      return daysOverdue < 1 ? '< 1 day' : daysOverdue
    },
  },
}
</script>

<template>
  <v-dialog
    :value="showDialog"
    max-width="950px"
    @input="(v) => v || closeDialog()"
  >
    <v-card>
      <v-card-title>
        <span class="text-h5 mb-1" data-testid="client-name">{{
          client._name
        }}</span>
      </v-card-title>
      <v-card-subtitle>
        <span>{{ $t('finance.overdueInvoices.featureName') }}</span>
      </v-card-subtitle>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="client.overdueInvoices"
          item-key="invoiceID"
          :sort-by="[
            'unchargedInterestExTax',
            'existingInterestChargedExTax',
            'daysOverdue',
          ]"
          :sort-desc="[true, true, true]"
          class="invoices-table elevation-1 mt-3"
        >
          <template v-slot:item.daysOverdue="{ item }">
            {{ formatDaysOverdue(item.daysOverdue) }}
          </template>
          <template v-slot:item.invoiceDate="{ item }">
            {{ localisedInvoiceDate(item.invoiceDate) }}
          </template>
          <template v-slot:item.invoiceValueExTax="{ item }">
            <i18n-n
              :value="item.invoiceValueExTax"
              format="currency"
              :locale="invoiceCenterLocale"
            ></i18n-n>
          </template>
          <template v-slot:item.unchargedInterestExTax="{ item }">
            <i18n-n
              :value="item.unchargedInterestExTax"
              format="currency"
              :locale="invoiceCenterLocale"
            ></i18n-n>
          </template>
          <template v-slot:item.existingInterestChargedExTax="{ item }">
            <span v-if="item.existingInterestChargedExTax > 0">
              <i18n-n
                :value="item.existingInterestChargedExTax"
                format="currency"
                :locale="invoiceCenterLocale"
              ></i18n-n
            ></span>
            <span v-else>-</span>
            <div v-if="item.existingInterestChargedExTax > 0" class="caption">
              <a
                href="#"
                @click.prevent.stop="onViewExistingChargesClick(item)"
                >{{ $t('finance.overdueInvoices.viewChargesLinkText') }}</a
              ></div
            >
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="closeDialog">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
    <InterestChargesDialog
      :show-dialog="showInterestChargesDialog"
      :charges="openedInterestCharges"
      :title="openedInterestChargesDialogTitle"
      @close-dialog="onCloseInterestChargesDialog"
    />
  </v-dialog>
</template>
